import { memo } from 'react';

import { ListingTile } from '@nocowanie/noc-api';

import { CUSTOM_IMAGE_SIZES } from '@nocowanie/core/consts';
import { CustomImageSize } from '@nocowanie/core/enums';

import { ACCOMMODATION_TILE } from '@app/consts';
import { MimeTypes } from '@app/enums';
import { useWpResizer } from '@app/hooks/useWpResizer';

import s from './accommodation-tile-lite.module.css';

interface TileImageProps {
    accommodationName: string;
    images: ListingTile['images'];
    isOTA: boolean;
    withHighPriority: boolean;
}

/** Same markup as `AccommodationTileImage`, without Chakra runtime styling */
const TileImageComponent = ({
    accommodationName,
    images,
    isOTA,
    withHighPriority,
}: TileImageProps) => {
    const wpresizer = useWpResizer();
    const mobileSize = CUSTOM_IMAGE_SIZES[CustomImageSize.AccommodationTileMobile];
    const desktopSize =
        CUSTOM_IMAGE_SIZES[
            isOTA
                ? CustomImageSize.AccommodationTileDesktopOTA
                : CustomImageSize.AccommodationTileDesktopInquiry
        ];

    const pictures = (images ?? [])
        .slice(0, ACCOMMODATION_TILE.IMAGE_COUNT)
        .map(image => image?.url)
        .filter((url): url is string => !!url);

    if (!pictures.length) {
        return (
            <div className={s.imagePlaceholder}>
                <div className={s.imagePlaceholderInner} />
            </div>
        );
    }

    return (
        <>
            {pictures.map((url, index) => {
                const withPriority = withHighPriority && index === 0;

                return (
                    <picture key={index}>
                        <source
                            srcSet={wpresizer([desktopSize.width, desktopSize.height], url)}
                            width={desktopSize.width}
                            height={desktopSize.height}
                            media={`(min-width: ${ACCOMMODATION_TILE.LAYOUT_BREAKPOINT_EM})`}
                            type={MimeTypes.webp}
                        />
                        <img
                            className={s.image}
                            src={wpresizer([mobileSize.resizeWidth, mobileSize.height], url)}
                            alt={accommodationName}
                            // Non-standard attribute kept for parity with the Chakra version
                            {...{ type: MimeTypes.webp }}
                            loading={withPriority ? undefined : 'lazy'}
                            fetchPriority={withPriority ? 'high' : undefined}
                            width={mobileSize.width}
                            height={mobileSize.height}
                        />
                    </picture>
                );
            })}
        </>
    );
};

export const TileImage = memo(
    TileImageComponent,
    (prev, next) =>
        prev.withHighPriority === next.withHighPriority &&
        prev.isOTA === next.isOTA &&
        prev.accommodationName === next.accommodationName &&
        prev.images?.length === next.images?.length &&
        (prev.images?.every((img, i) => img?.url === next.images?.[i]?.url) ?? true),
);
